import { useEffect, useLayoutEffect, useState, type RefObject } from 'react';

/*
  Одно выделение на уровень меню (R23): прямоугольник под активным пунктом, который переезжает,
  а не мигает. Замер — наблюдателем размеров: он срабатывает и сразу, и на каждом кадре, пока
  меню меняет ширину, поэтому выделение не отстаёт от пункта. Переезд включается только после
  первого замера — при первом показе выделение просто проявляется на месте.
*/

export interface SlidingBox {
  top: number;
  left: number;
  width: number;
  height: number;
  visible: boolean;
}

const HIDDEN: SlidingBox = { top: 0, left: 0, width: 0, height: 0, visible: false };

/**
 * @param containerRef — блок меню; выделение позиционируется относительно него.
 * @param selector — как найти активный пункт внутри блока.
 * @param measureKey — меняется, когда меняется активный пункт или вид меню.
 */
export function useSlidingBox(
  containerRef: RefObject<HTMLElement | null>,
  selector: string,
  measureKey: string,
): { box: SlidingBox; ready: boolean } {
  const [box, setBox] = useState<SlidingBox>(HIDDEN);
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const measure = () => {
      const target = container.querySelector<HTMLElement>(selector);
      if (!target) {
        setBox((b) => (b.visible ? { ...b, visible: false } : b));
        return;
      }
      const c = container.getBoundingClientRect();
      const t = target.getBoundingClientRect();
      setBox({ top: t.top - c.top, left: t.left - c.left, width: t.width, height: t.height, visible: true });
    };
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    const target = container.querySelector<HTMLElement>(selector);
    if (target) ro.observe(target);
    return () => ro.disconnect();
  }, [containerRef, selector, measureKey]);

  useEffect(() => {
    if (!box.visible || ready) return;
    const t = window.setTimeout(() => setReady(true), 50);
    return () => window.clearTimeout(t);
  }, [box.visible, ready]);

  return { box, ready };
}
